// Centralized Supabase client
import { supabase } from '@/app/lib/supabase';
import Papa from 'papaparse';
import { getCurrentUser } from '@/app/lib/supabase'
import {
  normalizeSemesterId as normalizeSemester,
  type SemesterId as SupportedSemester,
} from '@/app/lib/semesters';
import type {
  UserData,
  CourseData,
  ScheduleData,
  CourseListData,
  ApiDegreePlanData,
  ScheduledCourse,
  SnipeRequest,
  NotificationData,
  OpenSection,
  Degree,
  DegreeData,
  UserProfessionalInfo,
  ResearchOutreachProject,
  EmailDraft,
  EmailGoal,
  ResearchProjectType,
  OutreachProjectStatus,
  EmailDraftStatus
} from "@/app/types/interfaces";
import type { SIRSProfessorStats, SIRSCourseStats, SIRSDepartmentStats } from '@/app/lib/sirsData';

// NOTE: All Supabase calls should use the shared client from app/lib/supabase

// Standard TTLs for API caching
const TTL = {
  THIRTY_SECONDS: 30 * 1000,
  ONE_MINUTE: 60 * 1000,
  FIVE_MINUTES: 5 * 60 * 1000,
  TEN_MINUTES: 10 * 60 * 1000,
  FIFTEEN_MINUTES: 15 * 60 * 1000,
  ONE_DAY: 24 * 60 * 60 * 1000,
} as const;

// Enhanced API request cache with better memory management
interface CacheEntry {
  data: any;
  timestamp: number;
  ttl: number;
  accessCount: number;
  lastAccessed: number;
}

const apiCache = new Map<string, CacheEntry>();
const MAX_CACHE_SIZE = 1000; // Maximum number of cache entries
const CACHE_CLEANUP_INTERVAL = 2 * 60 * 1000; // Clean up every 2 minutes

export function clearAllApiCaches() {
  apiCache.clear();
  profileCache.clear();
}

function invalidateUserCaches(userId: string) {
  apiCache.delete(`user_${userId}`);
  // Also clear any update-related cached entries to avoid stale reads
  for (const key of apiCache.keys()) {
    if (key.startsWith('user_update_')) apiCache.delete(key);
  }
  // Reset profile cache below
  profileCache.delete(userId);
}

function invalidateCacheByPrefix(prefix: string) {
  if (typeof window !== 'undefined' && /^(schedules_|degree_plans_)/.test(prefix)) {
    try {
      for (const key of Object.keys(localStorage)) {
        if (/^scarlet-dashboard-(schedule|summary)-v\d+:/.test(key)) localStorage.removeItem(key);
      }
      window.dispatchEvent(new Event('scarlet-dashboard-invalidated'));
    } catch { /* Device snapshots are optional. */ }
  }

  for (const key of apiCache.keys()) {
    if (key.startsWith(prefix)) apiCache.delete(key);
  }
}

export function clearDegreeSearchCaches() {
  // Clear all degree-related API caches
  invalidateCacheByPrefix('degrees_');
  console.log('[clearDegreeSearchCaches] Cleared all degree-related API caches');
}

function invalidateCacheKeys(keys: string[]) {
  for (const key of keys) apiCache.delete(key);
}

// Enhanced cache management with LRU-like behavior
function manageCacheSize() {
  if (apiCache.size <= MAX_CACHE_SIZE) return;

  // Sort entries by last accessed time and remove oldest
  const entries = Array.from(apiCache.entries())
    .sort(([, a], [, b]) => a.lastAccessed - b.lastAccessed);

  const toRemove = entries.slice(0, Math.floor(MAX_CACHE_SIZE * 0.2)); // Remove 20% of oldest entries
  toRemove.forEach(([key]) => apiCache.delete(key));
}

async function cachedApiCall<T>(
  key: string,
  apiFunction: () => Promise<T>,
  ttl: number = TTL.TEN_MINUTES // default 10 minutes to reduce spam
): Promise<T> {
  const now = Date.now();
  const cached = apiCache.get(key);

  if (cached && now - cached.timestamp < cached.ttl) {
    // Update access statistics
    cached.accessCount++;
    cached.lastAccessed = now;
    return cached.data;
  }

  try {
    const data = await apiFunction();

    // Manage cache size before adding new entry
    manageCacheSize();

    apiCache.set(key, {
      data,
      timestamp: now,
      ttl,
      accessCount: 1,
      lastAccessed: now
    });
    return data;
  } catch (error) {
    // Return cached data even if expired if available
    if (cached) {
      console.warn(`API call failed for ${key}, using cached data`);
      cached.accessCount++;
      cached.lastAccessed = now;
      return cached.data;
    }
    throw error;
  }
}

// Enhanced cache cleanup with access-based cleanup
setInterval(() => {
  const now = Date.now();
  const toDelete: string[] = [];

  for (const [key, entry] of apiCache.entries()) {
    // Remove expired entries
    if (now - entry.timestamp > entry.ttl) {
      toDelete.push(key);
    }
    // Remove rarely accessed entries (accessed less than 2 times and older than 5 minutes)
    else if (entry.accessCount < 2 && now - entry.lastAccessed > 5 * 60 * 1000) {
      toDelete.push(key);
    }
  }

  toDelete.forEach(key => apiCache.delete(key));

  // Also manage cache size
  manageCacheSize();
}, CACHE_CLEANUP_INTERVAL);

// =============================================
// CAMPUS EVENTS
// =============================================

export interface EventsFiltersParams {
  source?: string | string[];
  date_range?: 'today' | 'week' | 'month';
  q?: string;
  category?: string;
  limit?: number;
  offset?: number;
}

export async function fetchCampusEvents(params: EventsFiltersParams = {}): Promise<{
  events: import('@/app/types/events').CampusEvent[];
  count: number;
  total: number | null;
}> {
  const query = new URLSearchParams();
  if (Array.isArray(params.source)) {
    params.source.forEach(source => query.append('source', source));
  } else if (params.source) {
    query.set('source', params.source);
  }
  if (params.date_range) query.set('date_range', params.date_range);
  if (params.q) query.set('q', params.q);
  if (params.category) query.set('category', params.category);
  if (params.limit) query.set('limit', String(params.limit));
  if (params.offset) query.set('offset', String(params.offset));

  const cacheKey = `events_${query.toString()}`;
  return cachedApiCall(
    cacheKey,
    async () => {
      const res = await fetch(`/api/events?${query}`);
      if (!res.ok) throw new Error(`Events API error: ${res.status}`);
      return res.json();
    },
    TTL.FIVE_MINUTES,
  );
}

// Cache warming function for frequently accessed data
export async function warmCache() {
  try {
    console.log('Warming up cache...');

    // Warm one API-backed client catalog instead of importing checked-in term
    // data into every client that uses this general-purpose API module.
    const { loadCatalogCourses } = await import('./courseCatalogClient');
    await loadCatalogCourses();

    // Pre-load degrees data
    await degreesApi.getAll();

    // Pre-load official course lists
    await courseListsApi.get_official_course_lists();

    console.log('Cache warming completed');
  } catch (error) {
    console.warn('Cache warming failed:', error);
  }
}

// SIRS API
export const sirsApi = {
  search: async (query: string, type: 'professor' | 'course' | 'department' | 'both' = 'both'): Promise<{
    results: {
      professors?: SIRSProfessorStats[];
      courses?: SIRSCourseStats[];
      departments?: SIRSDepartmentStats[];
    }
  }> => {
    return cachedApiCall(`sirs_search_${encodeURIComponent(query)}_${type}`, async () => {
      const response = await fetch(
        `/api/sirs/search?q=${encodeURIComponent(query)}&type=${type}`
      );
      if (!response.ok) throw new Error('Search failed');
      return await response.json();
    }, TTL.ONE_MINUTE);
  },

  getFeatured: async (signal?: AbortSignal): Promise<{
    departments: SIRSDepartmentStats[];
    professors: SIRSProfessorStats[];
  }> => {
    return cachedApiCall('sirs_featured', async () => {
      const response = await fetch('/api/sirs/featured', { signal });
      if (!response.ok) throw new Error('Failed to load featured data');
      return await response.json();
    }, TTL.ONE_DAY);
  }
};

// Users API
export const usersApi = {
  /** Owner profile read for preservation-sensitive writes; no cache or default writes. */
  getFresh: async (userId: string): Promise<unknown> => {
    if (!userId) throw new Error('An account is required to read academic data');
    const { data, error } = await supabase.functions.invoke('users', {
      body: { action: 'get', userId },
    });
    if (error) throw error;
    return data;
  },
  /** Replace the owner's academic record only if the fresh raw value is still current. */
  updateAcademicDataForOwner: async (
    userId: string,
    payload: Record<string, unknown>,
    expectedRaw: unknown,
  ): Promise<{ status: 'saved'; uid: string } | { status: 'conflict' }> => {
    if (!userId) throw new Error('An account is required to save academic data');
    if (expectedRaw === undefined) throw new Error('An expected academic record is required');
    const { data, error } = await supabase.rpc('cas_user_previous_classes', {
      p_user_id: userId,
      p_expected_previous_classes: expectedRaw,
      p_new_previous_classes: payload,
    });
    if (error) throw error;
    if (Array.isArray(data) && data.length === 0) return { status: 'conflict' };
    if (!Array.isArray(data) || data.length !== 1 || !data[0] || data[0].uid !== userId) {
      throw new Error('The academic save owner could not be verified');
    }
    apiCache.delete(`user_${userId}`);
    profileCache.delete(userId);
    return { status: 'saved', uid: userId };
  },
  get: async (userId: string) => {
    return cachedApiCall(`user_${userId}`, async () => {
      const { data, error } = await supabase.functions.invoke('users', {
        body: { action: 'get', userId }
      })
      if (error) throw error

      // get the data in preferences_json and if its empty change the default preferences
      if (!data[0].preferences_json) {
        data.preferences_json = { "availability": [[true, true, true, true, true, true, true, true, true, true, true, true], [true, true, true, true, true, true, true, true, true, true, true, true], [true, true, true, true, true, true, true, true, true, true, true, true], [true, true, true, true, true, true, true, true, true, true, true, true], [true, true, true, true, true, true, true, true, true, true, true, true]], "preferredCampuses": ["college ave", "livi", "busch", "cook/doug", "online"], "earliestStartTime": 540, "latestEndTime": 1320, "minimumProfessorRating": 0, "priorities": [{ "name": "campus", "value": 21, "color": "bg-pink-500" }, { "name": "professor ratings", "value": 21, "color": "bg-purple-500" }, { "name": "times", "value": 58, "color": "bg-blue-500" }], "campusPreferences": [{ "name": "college ave", "value": 36, "color": "bg-pink-500" }, { "name": "livi", "value": 9, "color": "bg-purple-500" }, { "name": "busch", "value": 15, "color": "bg-indigo-500" }, { "name": "cook/doug", "value": 30, "color": "bg-blue-500" }, { "name": "online", "value": 10, "color": "bg-teal-500" }] };

        await usersApi.update({ p_preferences_json: data.preferences_json });
      }
      return data
    });
  },
  update: async (updates: any) => {
    return cachedApiCall(`user_update_${JSON.stringify(updates)}`, async () => {
      // if preferences_json is not in the updates, add it
      /* if (!updates.p_preferences_json) {
        updates.p_preferences_json = {"availability":[[true,true,true,true,true,true,true,true,true,true,true,true],[true,true,true,true,true,true,true,true,true,true,true,true],[true,true,true,true,true,true,true,true,true,true,true,true],[true,true,true,true,true,true,true,true,true,true,true,true],[true,true,true,true,true,true,true,true,true,true,true,true]],"preferredCampuses":["college ave","livi","busch","cook/doug","online"],"earliestStartTime":540,"latestEndTime":1320,"minimumProfessorRating":0,"priorities":[{"name":"campus","value":21,"color":"bg-pink-500"},{"name":"professor ratings","value":21,"color":"bg-purple-500"},{"name":"times","value":58,"color":"bg-blue-500"}],"campusPreferences":[{"name":"college ave","value":36,"color":"bg-pink-500"},{"name":"livi","value":9,"color":"bg-purple-500"},{"name":"busch","value":15,"color":"bg-indigo-500"},{"name":"cook/doug","value":30,"color":"bg-blue-500"},{"name":"online","value":10,"color":"bg-teal-500"}]};
      } */

      // check if previous_classes is a string, if it is, parse it as json
      if (typeof updates.p_previous_classes === 'string') {
        updates.p_previous_classes = JSON.parse(updates.previous_classes);
      }

      const { data, error } = await supabase.functions.invoke('users', {
        body: { action: 'update', ...updates }
      })
      if (error) throw error
      try {
        const currentUser = await getCurrentUser();
        if (currentUser?.id) invalidateUserCaches(currentUser.id);
      } catch {
        // no-op if we cannot determine current user; caches will expire by TTL
      }
      return data
    }, 1 * 60 * 1000); // 1 minute cache for updates
  },
  deleteAccount: async (userId: string) => {
    const { data, error } = await supabase.functions.invoke('delete-account', {
      body: { action: 'delete_account', userId }
    })
    if (error) throw error
    return data
  }
}

// Google OAuth token helpers (persisted in Supabase via Edge Function)
export const googleTokensApi = {
  get: async (): Promise<{ access_token?: string; expires_at?: string } | null> => {
    const { data, error } = await supabase.functions.invoke('users', {
      body: { action: 'get_google_token' }
    });
    if (error) return null;
    return data as { access_token?: string; expires_at?: string };
  },
  save: async (payload: { access_token?: string; refresh_token?: string; expires_at?: string }) => {
    const { error } = await supabase.functions.invoke('users', {
      body: { action: 'save_google_token', ...payload }
    });
    if (error) throw error;
  }
}

const profileCache = new Map<string, { data: any; timestamp: number }>();
const PROFILE_CACHE_TTL = TTL.TEN_MINUTES;

export const getUserProfileData = async (userId: string) => {
  const now = Date.now();
  const cached = profileCache.get(userId);
  if (cached && now - cached.timestamp < PROFILE_CACHE_TTL) {
    return cached.data;
  }

  const { data, error } = await supabase.functions.invoke('get-user-profile-data', {
    body: { userId: userId }
  });
  if (error) {
    console.error('Error fetching user profile data:', error);
    throw error;
  }

  profileCache.set(userId, {
    data,
    timestamp: now,
  });
  return data;
};

// Schedules API
export const schedulesApi = {
  create: async (scheduleData: any) => {
    const { data, error } = await supabase.functions.invoke('schedules', {
      body: { action: 'create', ...scheduleData }
    })
    if (error) throw error
    invalidateCacheByPrefix('schedules_');
    return data
  },
  get: async (scheduleId: string) => {
    return cachedApiCall(`schedules_get_${scheduleId}`, async () => {
      const { data, error } = await supabase.functions.invoke('schedules', {
        body: { action: 'get', scheduleId }
      })
      if (error) throw error
      return data
    }, TTL.TEN_MINUTES)
  },
  get_all_user_schedules: async (userId: string) => {
    return cachedApiCall(`schedules_user_${userId}`, async () => {
      const { data, error } = await supabase.functions.invoke('schedules', {
        body: { action: 'get_all_user_schedules', userId }
      })
      if (error) throw error
      return data
    }, TTL.FIVE_MINUTES)
  },
  getPublicSchedulesByUser: async (userId: string) => {
    return cachedApiCall(`schedules_public_user_${userId}`, async () => {
      const { data, error } = await supabase.functions.invoke('schedules', {
        body: { action: 'get_public_schedules_by_user', userId }
      })
      if (error) throw error
      return data
    }, TTL.TEN_MINUTES)
  },
  update: async (scheduleId: string, updates: any) => {
    // update the updated_at timestamp to the current date
    updates.updated_at = new Date().toISOString();
    const { data, error } = await supabase.functions.invoke('schedules', {
      body: { action: 'update', scheduleId, ...updates }
    })
    if (error) throw error
    invalidateCacheKeys([
      `schedules_get_${scheduleId}`,
    ]);
    invalidateCacheByPrefix('schedules_user_');
    invalidateCacheByPrefix('schedules_public_user_');
    return data
  },
  delete: async (scheduleId: string) => {
    const { data, error } = await supabase.functions.invoke('schedules', {
      body: { action: 'delete', scheduleId }
    })
    if (error) throw error
    invalidateCacheKeys([
      `schedules_get_${scheduleId}`,
    ]);
    invalidateCacheByPrefix('schedules_user_');
    invalidateCacheByPrefix('schedules_public_user_');
    return data
  },
  copy: async (scheduleId: string, newName?: string, isPublic: boolean = false) => {
    const { data, error } = await supabase.functions.invoke('schedules', {
      body: { action: 'copy', scheduleId, newName, isPublic }
    })
    if (error) throw error
    invalidateCacheByPrefix('schedules_user_');
    invalidateCacheByPrefix('schedules_public_user_');
    return data
  }
}

// WebReg OCR API - uses Groq vision to extract course codes from images
export const webregOcrApi = {
  extractCourses: async (imageBase64: string): Promise<{
    success: boolean;
    courses: Array<{ courseCode: string; section: string; index: string }>;
    rawResponse?: string;
    error?: string;
  }> => {
    const { data, error } = await supabase.functions.invoke('webreg-ocr', {
      body: { imageBase64 }
    });
    if (error) throw error;
    return data;
  }
}

// Friendships API
export const friendshipsApi = {
  create: async (targetUserId: string) => {
    const { data, error } = await supabase.functions.invoke('friendships', {
      body: { action: 'create', targetUserId }
    })
    if (error) throw error
    invalidateCacheByPrefix('friendships_');
    return data
  },
  get: async (friendshipId: string) => {
    return cachedApiCall(`friendships_get_${friendshipId}`, async () => {
      const { data, error } = await supabase.functions.invoke('friendships', {
        body: { action: 'get', friendshipId }
      })
      if (error) throw error
      return data
    }, TTL.ONE_MINUTE)
  },
  getAllUserFriendships: async (userId: string) => {
    return cachedApiCall(`friendships_user_${userId}`, async () => {
      const { data, error } = await supabase.functions.invoke('friendships', {
        body: { action: 'get_user_friendships', userId }
      })
      if (error) throw error
      return data
    }, TTL.ONE_MINUTE)
  },
  updateStatus: async (friendshipId: string, status: string) => {
    const { data, error } = await supabase.functions.invoke('friendships', {
      body: { action: 'update', friendshipId, status }
    })
    if (error) throw error
    invalidateCacheKeys([
      `friendships_get_${friendshipId}`,
    ]);
    invalidateCacheByPrefix('friendships_user_');
    return data
  },
  delete: async (friendshipId: string) => {
    const { data, error } = await supabase.functions.invoke('friendships', {
      body: { action: 'delete', friendshipId }
    })
    if (error) throw error
    invalidateCacheKeys([
      `friendships_get_${friendshipId}`,
    ]);
    invalidateCacheByPrefix('friendships_user_');
    return data
  },
  searchUsers: async (email: string) => {
    return cachedApiCall(`friendships_search_${email}`, async () => {
      const { data, error } = await supabase.functions.invoke('friendships', {
        body: { action: 'search_users', email }
      })
      if (error) throw error
      return data
    }, TTL.THIRTY_SECONDS);
  },
  testUsersAccess: async () => {
    return cachedApiCall('friendships_test_access', async () => {
      const { data, error } = await supabase.functions.invoke('friendships', {
        body: { action: 'test_users_access' }
      })
      if (error) throw error
      return data
    }, TTL.ONE_MINUTE);
  }
  ,
  getSuggestedFriends: async () => {
    const key = `friendships_suggested`
    return cachedApiCall(key, async () => {
      const { data, error } = await supabase.functions.invoke('friendships', {
        body: { action: 'get_suggested_friends' }
      })
      if (error) throw error
      return data
    }, TTL.THIRTY_SECONDS)
  }
};

// Degree Plans API
export const degreePlansApi = {
  create: async (planData: any) => {
    const { data, error } = await supabase.functions.invoke('degree-plans', {
      body: { action: 'create', ...planData }
    })
    if (error) throw error
    invalidateCacheByPrefix('degree_plans_');
    return data
  },
  get: async (planId: number) => {
    return cachedApiCall(`degree_plans_get_${planId}`, async () => {
      const { data, error } = await supabase.functions.invoke('degree-plans', {
        body: { action: 'get', planId }
      })
      if (error) throw error
      return data
    }, TTL.TEN_MINUTES)
  },
  getAllUserPlans: async (userId: string) => {
    return cachedApiCall(`degree_plans_user_${userId}`, async () => {
      const { data, error } = await supabase.functions.invoke('degree-plans', {
        body: { action: 'get_user_plans', userId }
      })
      if (error) throw error
      return data
    }, TTL.TEN_MINUTES)
  },
  update: async (planId: number, updates: any) => {
    const { data, error } = await supabase.functions.invoke('degree-plans', {
      body: { action: 'update', planId, ...updates }
    })
    if (error) throw error
    invalidateCacheKeys([`degree_plans_get_${planId}`]);
    invalidateCacheByPrefix('degree_plans_user_');
    return data
  },
  delete: async (planId: number) => {
    const { data, error } = await supabase.functions.invoke('degree-plans', {
      body: { action: 'delete', planId }
    })
    if (error) throw error
    invalidateCacheKeys([`degree_plans_get_${planId}`]);
    invalidateCacheByPrefix('degree_plans_user_');
    return data
  }
}


// Share only concurrent requests for the exact same legacy raw ID. Settled
// misses/errors remain retryable; the existing positive cache stays unchanged.
const fallbackCourseNameRequests = new Map<string, Promise<{ title: string | null; offered: boolean }>>();

// Fallback course name resolver using server CSV API
export const fallbackCourseNames = {
  cache: new Map<string, { title: string; credits?: string | null }>(),
  async getName(courseId: string): Promise<{ title: string | null; offered: boolean }> {
    const cached = this.cache.get(courseId);
    if (cached) return { title: cached.title, offered: false };
    const pending = fallbackCourseNameRequests.get(courseId);
    if (pending) return pending;
    const request = (async () => {
      try {
        const url = `/api/courses/fallback-name?id=${encodeURIComponent(courseId)}`;
        const res = await fetch(url, { next: { revalidate: 60 * 60 } });
        if (!res.ok) return { title: null, offered: false };
        const data = await res.json();
        if (data?.found && data?.title) {
          this.cache.set(courseId, { title: data.title, credits: data.credits });
          return { title: data.title, offered: false };
        }
        return { title: null, offered: false };
      } catch {
        return { title: null, offered: false };
      }
    })().finally(() => {
      fallbackCourseNameRequests.delete(courseId);
    });
    fallbackCourseNameRequests.set(courseId, request);
    return request;
  }
};

// Add these functions to fetch open sections and core requirements
export const openSectionsService = {

  // Per-term cache: term_id -> set of open section index strings
  openSectionsCache: new Map<string, Set<string>>(),
  lastFetchTime: new Map<string, number>(),
  CACHE_DURATION: 5 * 60 * 1000, // 5 minutes

  async refreshOpenSections(termId: string = 'fall-2026'): Promise<void> {
    const now = Date.now();
    const last = this.lastFetchTime.get(termId) ?? 0;
    if (now - last < this.CACHE_DURATION) {
      return;
    }

    try {
      const sections = await openSectionsApi.getAll(termId);
      const sectionSet = new Set(sections.map((section) => section.toString()));
      this.openSectionsCache.set(termId, sectionSet);
      this.lastFetchTime.set(termId, now);
    } catch (error) {
      const detail =
        error && typeof error === 'object' && 'message' in error
          ? String((error as { message: unknown }).message)
          : String(error);
      console.error('Failed to refresh open sections:', { termId, detail, error });
    }
  },

  isOpen(index: string, termId: string = 'fall-2026'): boolean {
    // if index has a dash, split it into two parts and check the first part
    if (index.includes('-')) {
      const parts = index.split('-');
      index = parts[0];
    }

    const currentSections = this.openSectionsCache.get(termId);
    return currentSections ? currentSections.has(index) : false;
  }
};

// // Devices API
// export const devicesApi = {
//   get: async () => {
//     // Placeholder for getting user devices
//     console.log("Getting user devices (not implemented)");
//     return Promise.resolve([]);
//   },
//   revoke: async (deviceId: string) => {
//     // Placeholder for revoking a device
//     console.log(`Revoking device ${deviceId} (not implemented)`);
//     return Promise.resolve();
//   },
// };

// Snipe Requests API
export const snipeRequestsApi = {
  create: async (
    userId: string,
    courseIndex: string,
    termId: SupportedSemester = 'fall-2026',
    courseNote?: string | null
  ) => {
    const trimmedNote = courseNote?.trim();
    const insertPayload: Record<string, any> = {
      user_id: userId,
      course_index: courseIndex,
      term_id: normalizeSemester(termId),
      status: 'active'
    };

    if (trimmedNote) {
      insertPayload.course_note = trimmedNote;
    }

    const { data, error } = await supabase
      .from('snipe_requests')
      .insert(insertPayload)
      .select()
      .single();

    if (error) throw error;
    invalidateCacheByPrefix(`snipe_requests_user_${userId}`);
    return data as SnipeRequest;
  },

  getAll: async (userId: string): Promise<SnipeRequest[]> => {
    return cachedApiCall(`snipe_requests_user_${userId}_all`, async () => {
      const { data, error } = await supabase
        .from('snipe_requests')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as SnipeRequest[];
    }, TTL.ONE_MINUTE);
  },

  getActive: async (userId: string, termId?: SupportedSemester): Promise<SnipeRequest[]> => {
    const normalizedTerm = termId ? normalizeSemester(termId) : null;
    return cachedApiCall(`snipe_requests_user_${userId}_active_${normalizedTerm ?? 'all'}`, async () => {
      let query = supabase
        .from('snipe_requests')
        .select('*')
        .eq('user_id', userId)
        .eq('status', 'active');

      if (normalizedTerm) {
        query = query.eq('term_id', normalizedTerm);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;
      return data as SnipeRequest[];
    }, TTL.ONE_MINUTE);
  },

  cancel: async (snipeId: number) => {
    const { data, error } = await supabase
      .from('snipe_requests')
      .update({ status: 'cancelled' })
      .eq('id', snipeId)
      .select()
      .single();

    if (error) throw error;
    invalidateCacheByPrefix('snipe_requests_user_');
    return data as SnipeRequest;
  },

  delete: async (snipeId: number) => {
    const { error } = await supabase
      .from('snipe_requests')
      .delete()
      .eq('id', snipeId);

    if (error) throw error;
    invalidateCacheByPrefix('snipe_requests_user_');
  },

  cancelAllActive: async (userId: string) => {
    const { data, error } = await supabase
      .from('snipe_requests')
      .update({ status: 'cancelled' })
      .eq('user_id', userId)
      .eq('status', 'active')
      .select();

    if (error) throw error;
    invalidateCacheByPrefix('snipe_requests_user_');
    return (data ?? []) as SnipeRequest[];
  },

  // Allows resniping: if an existing row exists for the user/index with any status,
  // ensure there is an 'active' one (reactivate or insert new)
  resnipe: async (
    userId: string,
    courseIndex: string,
    termId: SupportedSemester = 'fall-2026',
    courseNote?: string | null
  ): Promise<SnipeRequest> => {
    const normalizedTerm = normalizeSemester(termId);
    const trimmedNote = courseNote?.trim();
    const updatePayload: Record<string, any> = {
      status: 'active',
      created_at: new Date().toISOString(),
      notified_at: null
    };

    if (trimmedNote) {
      updatePayload.course_note = trimmedNote;
    }

    // Try to update an existing non-active request to active
    const { data: updated, error: updateError } = await supabase
      .from('snipe_requests')
      .update(updatePayload)
      .eq('user_id', userId)
      .eq('course_index', courseIndex)
      .eq('term_id', normalizedTerm)
      .neq('status', 'active')
      .select()
      .single();

    if (!updateError && updated) {
      invalidateCacheByPrefix(`snipe_requests_user_${userId}`);
      return updated as SnipeRequest;
    }

    // If no existing row to update, insert a new one
    const insertPayload: Record<string, any> = {
      user_id: userId,
      course_index: courseIndex,
      term_id: normalizedTerm,
      status: 'active'
    };

    if (trimmedNote) {
      insertPayload.course_note = trimmedNote;
    }

    const { data, error } = await supabase
      .from('snipe_requests')
      .insert(insertPayload)
      .select()
      .single();
    if (error) throw error;
    invalidateCacheByPrefix(`snipe_requests_user_${userId}`);
    return data as SnipeRequest;
  }
};

// Snipe stats API (Edge Function)
export const snipeStatsApi = {
  countByIndex: async (index: string, termId: SupportedSemester = 'fall-2026'): Promise<number> => {
    const normalizedTerm = normalizeSemester(termId);
    const key = `snipe_stats_${normalizedTerm}_${index}`;
    const data = await cachedApiCall(key, async () => {
      const { data, error } = await supabase.functions.invoke('snipe-stats', {
        body: { action: 'count_by_index', index, termId: normalizedTerm }
      });
      if (error) throw error;
      return (data?.counts?.[index] ?? 0) as number;
    }, TTL.ONE_MINUTE);
    return data as number;
  },
  countsForIndexes: async (
    indexes: string[],
    termId: SupportedSemester = 'fall-2026'
  ): Promise<Record<string, number>> => {
    const normalizedTerm = normalizeSemester(termId);
    const key = `snipe_stats_many_${normalizedTerm}_${indexes.sort().join(',')}`;
    return cachedApiCall(key, async () => {
      const { data, error } = await supabase.functions.invoke('snipe-stats', {
        body: { action: 'counts_for_indexes', indexes, termId: normalizedTerm }
      });
      if (error) throw error;
      return (data?.counts ?? {}) as Record<string, number>;
    }, TTL.ONE_MINUTE);
  }
}

// Notifications API
export const notificationsApi = {
  getAll: async (userId: string): Promise<NotificationData[]> => {
    return cachedApiCall(`notifications_user_${userId}_all`, async () => {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as NotificationData[];
    }, TTL.THIRTY_SECONDS);
  },

  getUnread: async (userId: string): Promise<NotificationData[]> => {
    return cachedApiCall(`notifications_user_${userId}_unread`, async () => {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', userId)
        .eq('is_read', false)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as NotificationData[];
    }, TTL.THIRTY_SECONDS);
  },

  markAsRead: async (notificationId: number) => {
    const { error } = await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('id', notificationId);

    if (error) throw error;
    invalidateCacheByPrefix('notifications_');
  },

  markAllAsRead: async (userId: string) => {
    const { error } = await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('user_id', userId)
      .eq('is_read', false);

    if (error) throw error;
    invalidateCacheByPrefix(`notifications_user_${userId}`);
  },

  delete: async (notificationId: number) => {
    const { error } = await supabase
      .from('notifications')
      .delete()
      .eq('id', notificationId);

    if (error) throw error;
    invalidateCacheByPrefix('notifications_');
  }
};

// Open Sections API
export const openSectionsApi = {
  getAll: async (termId: string = 'fall-2026'): Promise<string[]> => {
    return cachedApiCall(`open_sections_${termId}`, async () => {
      const { data, error } = await supabase
        .from('open_sections')
        .select('sections')
        .eq('term_id', termId)
        .maybeSingle();

      if (error) throw error;
      if (!data || !Array.isArray(data.sections)) {
        throw new Error('Section availability snapshot is unavailable for this term.');
      }
      return data.sections as string[];
    }, TTL.TEN_MINUTES);
  },

  isOpen: async (termId: string = 'fall-2026', courseIndex: string): Promise<boolean> => {
    return cachedApiCall(`open_sections_${termId}_${courseIndex}`, async () => {
      const { data, error } = await supabase
        .from('open_sections')
        .select('sections')
        .eq('term_id', termId)
        .maybeSingle();

      if (error) throw error;

      const sections = (data?.sections || []) as string[];
      return sections.includes(courseIndex);
    }, TTL.TEN_MINUTES);
  },

  invalidateCache: (termId: string = 'fall-2026') => {
    invalidateCacheByPrefix(`open_sections_${termId}`);
  }
};

// Degrees API
export const degreesApi = {
  getAll: async (): Promise<Degree[]> => {
    return cachedApiCall('degrees_all', async () => {
      const data = await getCsvData('/degrees.csv');
      return data
        .filter((d: any) => d && d.ID && d.Name) // Filter out empty or incomplete rows
        .map((d: any) => ({
          ...d,
          requirements: d.requirements ? JSON.parse(d.requirements) : null,
        })) as Degree[];
    }, TTL.ONE_DAY);
  },

  getById: async (id: number): Promise<Degree | null> => {
    return cachedApiCall(`degrees_id_${id}`, async () => {
      const data = await getCsvData('/degrees.csv');
      const degree = data.find((d: any) => d.ID === id);
      if (degree) {
        return {
          ...degree,
          requirements: degree.requirements ? JSON.parse(degree.requirements) : null,
        } as Degree;
      }
      return null;
    }, TTL.ONE_DAY);
  },

  search: async (query: string): Promise<Degree[]> => {
    const key = `degrees_search_${encodeURIComponent(query)}`;
    return cachedApiCall(key, async () => {
      const data = await getCsvData('/degrees.csv');
      const queryLower = query.toLowerCase().trim();

      // Handle comma-separated queries - search for any of them
      const queryParts = queryLower.split(',').map(q => q.trim()).filter(q => q.length > 0);

      return data
        .filter((d: any) => {
          const name = (d.Name?.toLowerCase() || '').trim();
          const code = (d["Degree Code"]?.toLowerCase() || '').trim();
          const searchText = `${name} ${code}`;

          // If comma-separated, match if any part matches
          if (queryParts.length > 1) {
            return queryParts.some(part => {
              const partWords = part.split(/\s+/).filter(w => w.length > 0);
              if (partWords.length === 1) {
                return searchText.includes(part);
              }
              // All words in this part must appear
              return partWords.every(word => searchText.includes(word));
            });
          }

          // Single query - split into words for better matching
          const queryWords = queryLower.split(/\s+/).filter(w => w.length > 0);

          // If single word, check if it's contained
          if (queryWords.length === 1) {
            return name.includes(queryLower) || code.includes(queryLower);
          }

          // For multiple words, check if all words appear (in any order)
          return queryWords.every(word => searchText.includes(word));
        })
        .map((d: any) => ({
          ...d,
          requirements: d.requirements ? JSON.parse(d.requirements) : null,
        })) as Degree[];
    }, TTL.ONE_DAY);
  },

  getByStartTerm: async (startTerm: string): Promise<Degree[]> => {
    return cachedApiCall(`degrees_start_term_${encodeURIComponent(startTerm)}`, async () => {
      const data = await getCsvData('/degrees.csv');
      return data
        .filter((d: any) => d["Start Term"] === startTerm)
        .map((d: any) => ({
          ...d,
          requirements: d.requirements ? JSON.parse(d.requirements) : null,
        })) as Degree[];
    }, TTL.ONE_DAY);
  },

  getDegreeRequirements: async (degreeId: number): Promise<DegreeData | null> => {
    const degree = await degreesApi.getById(degreeId);
    return degree?.requirements || null;
  }
};

// Degree ↔ Course List mappings API
export interface DegreeCourseListMapping {
  id: number;
  name: string; // requirement id/name like nb01_core_mn_F19
  course_list_id: number; // references public."course-lists".id
}

// Removed degreeCourseListsApi - now using degree_course_lists_direct.csv directly
// The direct CSV is loaded in components using fetch('/degree_course_lists_direct.csv')
export const degreeCourseListsApi = {
  getAll: async (): Promise<DegreeCourseListMapping[]> => {
    // Deprecated: Use degree_course_lists_direct.csv directly instead
    console.warn('degreeCourseListsApi.getAll() is deprecated. Use degree_course_lists_direct.csv directly.');
    return [];
  },
};

// Course Lists API
export const courseListsApi = {
  create: async (listData: any) => {
    const { data, error } = await supabase.functions.invoke('course-lists', {
      body: { action: 'create', ...listData }
    })
    if (error) throw error
    invalidateCacheByPrefix('course_lists_');
    return data
  },
  get: async (listId: number) => {
    return cachedApiCall(`course_lists_get_${listId}`, async () => {
      const { data, error } = await supabase.functions.invoke('course-lists', {
        body: { action: 'get', listId }
      });
      if (error) throw error;
      return data;
    }, TTL.TEN_MINUTES);
  },
  get_all_user_course_lists: async (userId: string) => {
    return cachedApiCall(`course_lists_user_${userId}`, async () => {
      const { data, error } = await supabase.functions.invoke('course-lists', {
        body: { action: 'get_all_user_course_lists' }
      });
      if (error) throw error;
      return data || [];
    }, TTL.TEN_MINUTES);
  },
  get_public_course_lists: async (searchTerm?: string) => {
    const key = `course_lists_public_${encodeURIComponent(searchTerm || '')}`;
    return cachedApiCall(key, async () => {
      const { data, error } = await supabase.functions.invoke('course-lists', {
        body: { action: 'get_public_course_lists', searchTerm }
      });
      if (error) throw error;
      return data || [];
    }, TTL.ONE_DAY);
  },
  get_official_course_lists: async () => {
    return cachedApiCall('course_lists_official', async () => {
      const { data, error } = await supabase.functions.invoke('course-lists', {
        body: { action: 'get_official_course_lists' }
      });
      if (error) throw error;
      return data || [];
    }, TTL.ONE_DAY);
  },
  duplicate: async (listId: number, userId: string) => {
    const { data, error } = await supabase.functions.invoke('course-lists', {
      body: { action: 'duplicate', listId, userId }
    })
    if (error) throw error
    invalidateCacheKeys([
      `course_lists_get_${listId}`,
      `course_lists_user_${userId}`,
    ]);
    invalidateCacheByPrefix('course_lists_public_');
    return data
  },
  update: async (listId: number, updates: any) => {
    const { data, error } = await supabase.functions.invoke('course-lists', {
      body: { action: 'update', listId, ...updates }
    })
    if (error) throw error
    invalidateCacheKeys([`course_lists_get_${listId}`]);
    invalidateCacheByPrefix('course_lists_user_');
    invalidateCacheByPrefix('course_lists_public_');
    return data
  },
  delete: async (listId: number) => {
    const { data, error } = await supabase.functions.invoke('course-lists', {
      body: { action: 'delete', listId }
    })
    if (error) throw error
    invalidateCacheKeys([`course_lists_get_${listId}`]);
    invalidateCacheByPrefix('course_lists_user_');
    invalidateCacheByPrefix('course_lists_public_');
    return data
  }
}

// Function to fetch and parse CSV data
const getCsvData = async (filePath: string): Promise<any[]> => {
  // Handle both browser and Node.js environments
  if (typeof window === 'undefined' && typeof process !== 'undefined') {
    // Node.js environment - use file system
    const path = await import('path');
    const fs = await import('fs');
    const absolutePath = filePath.startsWith('/')
      ? path.join(process.cwd(), 'public', filePath)
      : path.resolve(process.cwd(), filePath);
    const text = fs.readFileSync(absolutePath, 'utf-8');
    return new Promise((resolve) => {
      Papa.parse(text, {
        header: true,
        dynamicTyping: true,
        complete: (results) => {
          resolve(results.data);
        },
      });
    });
  }

  // Browser environment - use fetch
  const response = await fetch(filePath);
  const text = await response.text();
  return new Promise((resolve) => {
    Papa.parse(text, {
      header: true,
      dynamicTyping: true,
      complete: (results) => {
        resolve(results.data);
      },
    });
  });
};

// Social API: aggregate friends and their public/primary schedules with 15-minute caching
export interface FriendWithPrimarySchedule {
  friend: {
    id: string;
    email: string;
    display_name?: string;
    name?: string;
    profile_picture_url?: string;
  };
  schedules: Array<{
    schedule_id: string;
    user_id: string;
    name: string;
    term_id?: string;
    description?: string;
    created_at?: string;
    updated_at?: string;
    classes: string[];
    is_public: boolean;
    owner_name?: string;
    is_primary?: boolean;
  }>;
  primarySchedule: {
    schedule_id: string;
    classes: string[];
    name: string;
    term_id: string;
  } | null;
}

export const socialApi = {
  getFriendsPrimarySchedules: async (userId: string): Promise<FriendWithPrimarySchedule[]> => {
    const cacheKey = `social_friends_primary_schedules_${userId}`;
    return cachedApiCall(cacheKey, async () => {
      // Get friendships (accepted only)
      const rawFriendships: Array<{ friendship: { status: string }; friend: any }> = await friendshipsApi.getAllUserFriendships(userId) as any;
      const acceptedFriends = (rawFriendships || [])
        .filter(f => (f.friendship?.status || '').toUpperCase() === 'ACCEPTED')
        .map(f => f.friend);

      // Fetch public schedules for each friend
      const results = await Promise.all(
        acceptedFriends.map(async (friend: any) => {
          const schedules = await schedulesApi.getPublicSchedulesByUser(friend.id);
          const primary = (schedules || []).find((s: any) => s.is_primary) || null;
          return {
            friend: {
              id: friend.id,
              email: friend.email,
              display_name: friend.display_name,
              name: friend.display_name || friend.name,
              profile_picture_url: friend.profile_picture_url,
            },
            schedules: schedules || [],
            primarySchedule: primary
              ? { schedule_id: primary.schedule_id, classes: primary.classes || [], name: primary.name || 'Schedule', term_id: primary.term_id }
              : null,
          } as FriendWithPrimarySchedule;
        })
      );

      return results;
    }, TTL.FIFTEEN_MINUTES);
  }
};

export const communityNotesApi = {
  getNotes: async (degree_id: number) => {
    const { data: { session } } = await supabase.auth.getSession();
    const response = await supabase.functions.invoke(`community-notes?degree_id=${degree_id}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${session?.access_token}`,
      },
    });

    if (response.error) {
      throw new Error('Failed to fetch notes');
    }
    return response.data;
  },
  createNote: async (note: { degree_id: number; content: string; parent_id?: number }) => {
    const { data: { session } } = await supabase.auth.getSession();
    const response = await supabase.functions.invoke('community-notes', {
      method: 'POST',
      body: note,
      headers: {
        Authorization: `Bearer ${session?.access_token}`,
      },
    });

    if (response.error) {
      throw new Error('Failed to create note');
    }
    return response.data;
  },
  vote: async (vote: { note_id: number; vote_type: 'up' | 'down' }) => {
    const { data: { session } } = await supabase.auth.getSession();
    const response = await supabase.functions.invoke('community-notes/vote', {
      method: 'POST',
      body: vote,
      headers: {
        Authorization: `Bearer ${session?.access_token}`,
      },
    });

    if (response.error) {
      throw new Error('Failed to vote');
    }
    return response.data;
  },
};

export const adminApi = {
  getStats: async (_options?: { forceRefresh?: boolean }) => {
    // Always authorize on the server. Privileged aggregates must not survive a role change
    // in an account-independent client cache.
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) throw new Error('Sign in required');
    const response = await supabase.functions.invoke('admin-stats', {
      method: 'POST', body: { action: 'get_stats' },
      headers: { Authorization: `Bearer ${session.access_token}` },
    });
    if (response.error) {
      const status = response.error.context?.status;
      throw new Error(status === 403 ? 'Admin access required' : status === 401 ? 'Sign in required' : 'Administration data is unavailable. Please try again.');
    }
    if (response.data?.error) throw new Error(String(response.data.error));
    if (response.data?.version !== 2) throw new Error('The administration service needs an update before this dashboard can load.');
    return response.data;
  },
};

export const researchOutreachApi = {
  // Manage selected projects
  async selectProject(
    userId: string,
    projectId: number,
    projectType: ResearchProjectType,
    projectData: any,
    recipientEmail: string,
    recipientName?: string
  ): Promise<ResearchOutreachProject> {
    const { data, error } = await supabase
      .from('research_outreach_projects')
      .upsert({
        user_id: userId,
        project_id: projectId,
        project_type: projectType,
        project_data: projectData,
        recipient_email: recipientEmail,
        recipient_name: recipientName || null,
        status: 'selected',
      }, {
        onConflict: 'user_id,project_id,project_type',
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async deselectProject(userId: string, projectId: number, projectType: ResearchProjectType): Promise<void> {
    const { error } = await supabase
      .from('research_outreach_projects')
      .delete()
      .eq('user_id', userId)
      .eq('project_id', projectId)
      .eq('project_type', projectType);

    if (error) throw error;
  },

  async getSelectedProjects(userId: string): Promise<ResearchOutreachProject[]> {
    const { data, error } = await supabase
      .from('research_outreach_projects')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async updateProjectStatus(
    userId: string,
    projectId: number,
    projectType: ResearchProjectType,
    status: OutreachProjectStatus
  ): Promise<void> {
    const { error } = await supabase
      .from('research_outreach_projects')
      .update({ status })
      .eq('user_id', userId)
      .eq('project_id', projectId)
      .eq('project_type', projectType);

    if (error) throw error;
  },

  // Generate email using AI
  async generateEmail(
    userId: string,
    projectId: number,
    projectType: ResearchProjectType,
    emailGoal: EmailGoal,
    customGoalText?: string
  ): Promise<{ draft_id: number; subject: string; body: string; email_goal: EmailGoal }> {
    const { data: { session } } = await supabase.auth.getSession();
    const response = await supabase.functions.invoke('generate-email', {
      method: 'POST',
      body: {
        project_id: projectId,
        project_type: projectType,
        email_goal: emailGoal,
        custom_goal_text: customGoalText || null,
      },
      headers: {
        Authorization: `Bearer ${session?.access_token}`,
      },
    });

    if (response.error) {
      const errorData = response.error as any;
      if (errorData.message === 'PROFESSIONAL_INFO_REQUIRED') {
        throw new Error('PROFESSIONAL_INFO_REQUIRED: Please upload your resume in Profile Settings to enable AI personalization.');
      }
      throw new Error(errorData.message || 'Failed to generate email');
    }
    const result = response.data as { draft_id: number; subject: string; body: string; email_goal: EmailGoal } | null;
    if (!result || !Number.isSafeInteger(result.draft_id) || result.draft_id <= 0) {
      throw new Error('Could not confirm the generated email draft was saved. Check drafts before trying again.');
    }
    return result;
  },

  // Manage email drafts
  async createDraft(
    userId: string,
    outreachProjectId: number,
    recipientEmail: string,
    recipientName: string | null,
    subject: string,
    body: string,
    emailGoal: EmailGoal,
    customGoalText: string | null,
    generatedContext: any
  ): Promise<EmailDraft> {
    const { data, error } = await supabase
      .from('email_drafts')
      .insert({
        user_id: userId,
        outreach_project_id: outreachProjectId,
        recipient_email: recipientEmail,
        recipient_name: recipientName,
        subject,
        body,
        email_goal: emailGoal,
        custom_goal_text: customGoalText,
        generated_context: generatedContext,
        status: 'draft',
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async getDrafts(userId: string): Promise<EmailDraft[]> {
    const { data, error } = await supabase
      .from('email_drafts')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async getDraft(draftId: number): Promise<EmailDraft> {
    const { data, error } = await supabase
      .from('email_drafts')
      .select('*')
      .eq('id', draftId)
      .single();

    if (error) throw error;
    return data;
  },

  async updateDraft(
    draftId: number,
    updates: Partial<Pick<EmailDraft, 'subject' | 'body' | 'status' | 'email_goal' | 'custom_goal_text'>>
  ): Promise<EmailDraft> {
    const { data, error } = await supabase
      .from('email_drafts')
      .update(updates)
      .eq('id', draftId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async deleteDraft(draftId: number): Promise<void> {
    const { error } = await supabase
      .from('email_drafts')
      .delete()
      .eq('id', draftId);

    if (error) throw error;
  },

  // Gmail integration
  async getGmailOAuthUrl(): Promise<string> {
    const { data: { session } } = await supabase.auth.getSession();
    const response = await supabase.functions.invoke('gmail-oauth', {
      method: 'POST',
      body: { action: 'initiate' },
      headers: {
        Authorization: `Bearer ${session?.access_token}`,
      },
    });

    if (response.error) {
      throw new Error(response.error.message || 'Failed to get Gmail OAuth URL');
    }
    return response.data.auth_url;
  },

  async createGmailDraft(
    draftId: number,
    recipientEmail: string,
    subject: string,
    body: string
  ): Promise<{ gmail_draft_id: string; draft_url: string }> {
    const { data: { session } } = await supabase.auth.getSession();
    const response = await supabase.functions.invoke('gmail-draft', {
      method: 'POST',
      body: {
        draft_id: draftId,
      },
      headers: {
        Authorization: `Bearer ${session?.access_token}`,
      },
    });

    if (response.error) {
      throw new Error(response.error.message || 'Failed to create Gmail draft');
    }
    return response.data;
  },

  async checkGmailConnection(): Promise<boolean> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user?.id) return false;

    // Check if user has Gmail OAuth tokens
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    const { data, error } = await supabase
      .from('user_oauth_tokens')
      .select('provider')
      .eq('user_id', user.id)
      .eq('provider', 'gmail')
      .single();

    return !error && !!data;
  },

  // Analytics/Stats
  async getOutreachStats(userId: string): Promise<{
    total_projects: number;
    total_drafts: number;
    sent_emails: number;
    by_status: Record<OutreachProjectStatus, number>;
  }> {
    const [projects, drafts] = await Promise.all([
      this.getSelectedProjects(userId),
      this.getDrafts(userId),
    ]);

    const byStatus: Record<OutreachProjectStatus, number> = {
      selected: 0,
      email_sent: 0,
      responded: 0,
      archived: 0,
    };

    projects.forEach(p => {
      byStatus[p.status] = (byStatus[p.status] || 0) + 1;
    });

    return {
      total_projects: projects.length,
      total_drafts: drafts.length,
      sent_emails: drafts.filter(d => d.status === 'sent').length,
      by_status: byStatus,
    };
  },
};

// Helper function to extract text from PDF (only works in browser)
async function extractTextFromPDF(file: File): Promise<string> {
  // Dynamically import pdfjs-dist to avoid SSR issues
  const pdfjsLib = await import('pdfjs-dist');

  // Configure PDF.js worker if in browser
  if (typeof window !== 'undefined') {
    pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';
  }

  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

  let fullText = '';
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const textContent = await page.getTextContent();

    // Preserve line breaks better by checking item positions
    let pageText = '';
    let lastY = -1;
    for (const item of textContent.items as any[]) {
      const currentY = item.transform?.[5] || 0;
      // If Y position changed significantly, it's likely a new line
      if (lastY !== -1 && Math.abs(currentY - lastY) > 5) {
        pageText += '\n';
      }
      pageText += item.str;
      // Add space if not already at end
      if (!item.str.endsWith(' ')) {
        pageText += ' ';
      }
      lastY = currentY;
    }

    fullText += pageText + '\n\n';
  }

  return fullText.trim();
}

export const professionalInfoApi = {
  // Get user's professional info
  async get(userId: string): Promise<UserProfessionalInfo | null> {
    const { data, error } = await supabase
      .from('user_professional_info')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();

    if (error) {
      throw error;
    }
    return data;
  },

  // Update or create professional info
  async upsert(userId: string, info: Partial<UserProfessionalInfo>): Promise<UserProfessionalInfo> {
    const { data, error } = await supabase
      .from('user_professional_info')
      .upsert({
        user_id: userId,
        ...info,
        updated_at: new Date().toISOString(),
      }, {
        onConflict: 'user_id',
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Extract text from PDF and store it
  async extractAndStoreDocument(
    userId: string,
    file: File,
    documentType: 'resume' | 'transcript'
  ): Promise<string> {
    // Extract text from PDF
    const extractedText = await extractTextFromPDF(file);

    // Update database with extracted text
    const updateField = documentType === 'resume'
      ? { resume_text: extractedText }
      : { transcript_text: extractedText };

    await this.upsert(userId, updateField);

    return extractedText;
  },

  // Clear document text
  async clearDocument(userId: string, documentType: 'resume' | 'transcript'): Promise<void> {
    const updateField = documentType === 'resume'
      ? { resume_text: null }
      : { transcript_text: null };

    await this.upsert(userId, updateField);
  },
};

// Stripe / Billing API
const PYTHON_SERVICE_URL =
  process.env.NEXT_PUBLIC_PYTHON_SERVICE_URL?.replace(/\/$/, '') ?? 'http://localhost:8000';

export const stripeApi = {
  /** Create a Stripe Checkout session and return the redirect URL. */
  createCheckoutSession: async (accessToken: string): Promise<string> => {
    const res = await fetch(`${PYTHON_SERVICE_URL}/stripe/create-checkout-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({}),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail ?? 'Failed to create checkout session');
    }
    const { url } = await res.json();
    return url as string;
  },

  /** Create a Stripe Customer Portal session and return the redirect URL. */
  getPortalUrl: async (accessToken: string): Promise<string> => {
    const res = await fetch(`${PYTHON_SERVICE_URL}/stripe/portal`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({}),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail ?? 'Failed to open billing portal');
    }
    const { url } = await res.json();
    return url as string;
  },

  /** Get the authenticated account's server-owned billing status. */
  getStatus: async (accessToken: string): Promise<{
    accessStatus: 'free' | 'active';
    accessUntil: string | null;
    subscriptionStatus: string | null;
    cancelAtPeriodEnd: boolean;
    currentPeriodEnd: string | null;
    hasBillingAccount: boolean;
  }> => {
    const res = await fetch(`${PYTHON_SERVICE_URL}/stripe/status`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!res.ok) throw new Error('Failed to fetch subscription status');
    return res.json();
  },
};
